import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import { BRAND_NAME } from '../brand';

import PageContainer from '../components/PageContainer';
import ModeBuilderWizard from '../modes/ModeBuilderWizard';
import ModeLimitBlock from '../modes/ModeLimitBlock';
import { domainsApi, ModeDefinitionCreatePayload } from '../api/domains';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import {
    parseModeLimitFromApiError,
    type ModeLimitDetail,
} from '../utils/modeLimitMessaging';

type BuilderTab = 'ai' | 'manual';

function formatApiValidationErrors(detail: unknown): string[] {
    if (!Array.isArray(detail)) {
        if (typeof detail === 'string' && detail.trim()) return [detail];
        if (detail && typeof detail === 'object' && 'message' in (detail as object)) {
            const message = (detail as { message?: string }).message;
            if (message) return [message];
        }
        return ['Could not create mode. Check your inputs and try again.'];
    }
    return detail.map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') {
            const loc = Array.isArray((entry as { loc?: unknown }).loc)
                ? (entry as { loc: unknown[] }).loc.join('.')
                : '';
            const msg = (entry as { msg?: string }).msg || 'Validation error';
            return loc ? `${loc}: ${msg}` : msg;
        }
        return 'Validation error';
    });
}

function parseAiBuilderError(error: unknown): string[] {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const code = (detail as { code?: string }).code;
        if (code === 'AI_CREDITS_EXHAUSTED') {
            const message =
                typeof (detail as { message?: string }).message === 'string'
                    ? (detail as { message: string }).message
                    : 'AI credits are exhausted for this cycle. You can still build a mode manually.';
            return [message];
        }
        if (code === 'MODE_BUILDER_UNAVAILABLE' || code === 'MODE_SUGGESTION_CAP_REACHED') {
            const message = (detail as { message?: string }).message;
            if (message) return [message];
        }
        if (code === 'MODE_BUILDER_VALIDATION_FAILED') {
            const message = (detail as { message?: string }).message;
            if (message) return [message];
        }
    }
    return formatApiValidationErrors(detail);
}

const ModeBuilderPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setActiveDomain, refresh } = useAppBootstrap();
    const [activeTab, setActiveTab] = useState<BuilderTab>('ai');
    const [aiDescription, setAiDescription] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiErrors, setAiErrors] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);
    const [modeLimitDetail, setModeLimitDetail] = useState<ModeLimitDetail | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
        null,
    );
    const [pendingNavigateTo, setPendingNavigateTo] = useState<string | null>(null);
    const aiIdempotencyKeyRef = useRef<string | null>(null);

    const returnTo = useMemo(() => {
        const raw = searchParams.get('returnTo');
        if (!raw || !raw.startsWith('/app/')) return '/app/profile';
        return raw;
    }, [searchParams]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refresh();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [refresh]);

    useEffect(() => {
        aiIdempotencyKeyRef.current = null;
    }, [aiDescription]);

    const handleSubmit = async (payload: ModeDefinitionCreatePayload) => {
        setSubmitting(true);
        setServerErrors([]);
        setModeLimitDetail(null);
        try {
            const created = await domainsApi.createDomain(payload);
            try {
                await setActiveDomain(created.key);
            } catch {
                // Mode was created; preference update failure should not block success UX.
            }
            setToast({ message: `Mode "${created.label}" created.`, severity: 'success' });
            setPendingNavigateTo(returnTo);
        } catch (error) {
            const limitDetail = parseModeLimitFromApiError(error);
            if (limitDetail) {
                setModeLimitDetail(limitDetail);
                return;
            }
            const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
                ?.detail;
            setServerErrors(formatApiValidationErrors(detail));
        } finally {
            setSubmitting(false);
        }
    };

    const handleAiGenerate = async () => {
        const trimmed = aiDescription.trim();
        if (!trimmed || aiGenerating) return;
        setAiGenerating(true);
        setAiErrors([]);
        try {
            if (!aiIdempotencyKeyRef.current) {
                aiIdempotencyKeyRef.current =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID()
                        : `mode-builder-${Date.now()}`;
            }
            const suggestion = await domainsApi.createModeSuggestion(trimmed, {
                idempotencyKey: aiIdempotencyKeyRef.current,
            });
            aiIdempotencyKeyRef.current = null;
            navigate(`/app/modes/suggestions/${suggestion.id}`, { replace: true });
        } catch (error) {
            setAiErrors(parseAiBuilderError(error));
        } finally {
            setAiGenerating(false);
        }
    };

    return (
        <PageContainer maxWidth="900px">
            <div className="glass-card border-none bg-theme-secondary p-6 md:p-8 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-teal-400">
                            Custom mode
                        </p>
                        <h1 className="text-2xl font-bold text-theme mt-1">Create a mode</h1>
                        <p className="text-sm text-theme-secondary mt-2 max-w-xl">
                            Describe your workflow for an AI-assisted draft, or build a configuration
                            manually. {BRAND_NAME} validates everything before anything is saved.
                        </p>
                    </div>
                    <Link
                        to={returnTo}
                        className="rounded-lg border border-theme px-4 py-2 text-sm text-theme-secondary hover:text-theme hover:bg-theme-card-hover"
                    >
                        Cancel
                    </Link>
                </div>

                <div
                    className="mb-6 flex gap-2 border-b border-theme pb-1"
                    role="tablist"
                    aria-label="Mode builder method"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'ai'}
                        className={`rounded-t-lg px-4 py-2 text-sm font-medium ${
                            activeTab === 'ai'
                                ? 'bg-theme-card text-theme border border-theme border-b-transparent'
                                : 'text-theme-secondary hover:text-theme'
                        }`}
                        onClick={() => setActiveTab('ai')}
                    >
                        AI assist
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'manual'}
                        className={`rounded-t-lg px-4 py-2 text-sm font-medium ${
                            activeTab === 'manual'
                                ? 'bg-theme-card text-theme border border-theme border-b-transparent'
                                : 'text-theme-secondary hover:text-theme'
                        }`}
                        onClick={() => setActiveTab('manual')}
                    >
                        Build manually
                    </button>
                </div>

                {activeTab === 'ai' ? (
                    <div role="tabpanel" aria-busy={aiGenerating} data-testid="MODE_BUILDER_AI_PANEL">
                        <p className="text-sm text-theme-secondary mb-4">
                            {BRAND_NAME} suggests a starting configuration — you review before anything is
                            saved.
                        </p>
                        <label htmlFor="mode-ai-description" className="block text-sm font-medium text-theme mb-2">
                            Describe your mode
                        </label>
                        <textarea
                            id="mode-ai-description"
                            className="w-full min-h-[120px] rounded-lg border border-theme bg-theme-primary px-3 py-2 text-sm text-theme"
                            placeholder="e.g. Track freelance client projects, proposals, and invoices from Upwork and email"
                            value={aiDescription}
                            onChange={(e) => setAiDescription(e.target.value)}
                            maxLength={500}
                            disabled={aiGenerating}
                        />
                        {aiErrors.length > 0 ? (
                            <div
                                className="mt-3 space-y-1"
                                role="alert"
                                data-testid="MODE_BUILDER_AI_ERRORS"
                            >
                                {aiErrors.map((message) => (
                                    <Alert key={message} severity="error" variant="outlined">
                                        {message}
                                    </Alert>
                                ))}
                            </div>
                        ) : null}
                        <div className="mt-4 flex flex-wrap gap-3">
                            <button
                                type="button"
                                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                                onClick={handleAiGenerate}
                                disabled={aiGenerating || !aiDescription.trim()}
                                data-testid="MODE_BUILDER_AI_GENERATE"
                            >
                                {aiGenerating ? 'Generating…' : 'Generate suggestion'}
                            </button>
                            <button
                                type="button"
                                className="rounded-lg border border-theme px-4 py-2 text-sm text-theme-secondary hover:text-theme"
                                onClick={() => setActiveTab('manual')}
                            >
                                Build manually instead
                            </button>
                        </div>
                    </div>
                ) : (
                    <div role="tabpanel">
                        {modeLimitDetail ? (
                            <div className="mb-6">
                                <ModeLimitBlock detail={modeLimitDetail} context="create" />
                            </div>
                        ) : null}

                        <ModeBuilderWizard
                            onSubmit={handleSubmit}
                            submitting={submitting}
                            serverErrors={serverErrors}
                            onStepChange={() => {
                                setServerErrors([]);
                                setModeLimitDetail(null);
                            }}
                        />
                    </div>
                )}
            </div>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={4500}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;
                    setToast(null);
                    if (pendingNavigateTo) {
                        navigate(pendingNavigateTo, { replace: true });
                        setPendingNavigateTo(null);
                    }
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => {
                        setToast(null);
                        if (pendingNavigateTo) {
                            navigate(pendingNavigateTo, { replace: true });
                            setPendingNavigateTo(null);
                        }
                    }}
                    severity={toast?.severity ?? 'success'}
                    variant="filled"
                    data-testid="MODE_BUILDER_TOAST"
                >
                    {toast?.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default ModeBuilderPage;
